// Card.jsx
import React, { useRef, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { RiArrowRightUpLine } from "react-icons/ri";

gsap.registerPlugin(ScrollTrigger);

const Card = ({ project }) => {
    const cardRef = useRef(null);
    const observerRef = useRef(null);
    const ctxRef = useRef(null);

    useEffect(() => {
        const el = cardRef.current;
        if (!el) return;

        ctxRef.current = gsap.context(() => {

            // 🎯 Ultra-smooth initial state
            gsap.set(el, {
                autoAlpha: 0,
                y: 80,
                filter: "blur(5px)"
            });

            const animateCard = () => {
                gsap.to(el, {
                    autoAlpha: 1,
                    y: 0,
                    filter: "blur(0px)",
                    duration: 0.5,
                    ease: "power4.out",
                    stagger: 0.4
                });
            };

            if (typeof ScrollTrigger !== "undefined") {
                ScrollTrigger.create({
                    trigger: el,
                    start: "top 90%",
                    once: true,
                    onEnter: animateCard
                });

                setTimeout(() => ScrollTrigger.refresh(), 300);
            } else {
                const io = new IntersectionObserver((entries, obs) => {
                    if (entries[0].isIntersecting) {
                        animateCard();
                        obs.disconnect();
                    }
                });
                observerRef.current = io;
                io.observe(el);
            }

        }, cardRef);

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
            if (ctxRef.current) ctxRef.current.revert();
        };
    }, []);

    return (
        <>
            {/* Prevent flash */}
            <style>{`
                .card-anim {
                    opacity: 0;
                    transform: translateY(80px);
                    filter: blur(5px);
                }
            `}</style>

            <a
                ref={cardRef}
                href={project.src}
                target="_blank"
                rel="noopener noreferrer"
                className="card-anim bg-background/50 border border-white/15 p-6 
                           rounded-xl overflow-hidden transition duration-400 
                           ease-in-out cursor-pointer group"
            >
                <div className="aspect-video overflow-hidden mb-4 border border-white/5 rounded-md">
                    <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover object-top 
                                   transition-transform duration-500 
                                   group-hover:scale-[1.05]"
                    />
                </div>

                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-2xl font-semibold text-text-light mb-1">
                            {project.title}
                        </h3>
                        <p className="text-text-light/70">{project.description}</p>
                    </div>

                    <p
                        className="text-white ml-4 p-2 text-3xl"
                    >
                        <RiArrowRightUpLine className="ArrowUp" />
                    </p>
                </div>
            </a>
        </>
    );
};

export default Card;
